
import { db, serverTimestamp } from "../../firebaseClient";
import type { CalendarEvent, CalendarEventAutoType } from "../types/calendar";
import firebase from "firebase/compat/app";
import { formatDate } from "../utils/dateUtils";

const CALENDAR_COL = "calendarEvents";

// --- Helpers ---

function toTimestamp(date: Date) {
  return firebase.firestore.Timestamp.fromDate(date);
}

function fromTimestamp(ts: any): Date {
  if (ts && typeof ts.toDate === 'function') return ts.toDate();
  if (ts instanceof Date) return ts;
  return new Date(ts); // Fallback string/number
}

export function addBusinessDays(start: Date, days: number): Date {
  const result = new Date(start);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay(); // 0: Sunday, 6: Saturday
    if (day !== 0 && day !== 6) {
      added++;
    }
  }
  return result;
}

export function subtractDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() - days);
  return result;
}

function addDays(dateStr: string, diff: number): string {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + diff);
  return formatDate(d);
}

// Convert Firestore doc to CalendarEvent object
function docToEvent(d: firebase.firestore.QueryDocumentSnapshot): CalendarEvent {
  const data = d.data();
  return {
    id: d.id,
    title: data.title || "(無標題)",
    
    mode: data.mode || 'ALL_DAY_RANGE',
    date: fromTimestamp(data.date),
    startDate: data.startDate, // YYYY-MM-DD
    endDate: data.endDate,     // YYYY-MM-DD
    startDateTime: data.startDateTime,
    endDateTime: data.endDateTime,
    allDay: data.allDay ?? true,

    category: data.category,
    subType: data.subType,
    sourceType: data.sourceType || 'manual',
    sourceId: data.sourceId || '',
    relatedRef: data.relatedRef,
    note: data.note,
    
    color: data.color,
    
    recurrence: data.recurrence || 'NONE',
    recurrenceUntil: data.recurrenceUntil,
    autoType: data.autoType
  } as CalendarEvent;
}

// Expand Recurring Events in Memory
function expandWeeklyEvents(events: CalendarEvent[], startRange: Date, endRange: Date): CalendarEvent[] {
    const expanded: CalendarEvent[] = [];
    const rangeStartStr = formatDate(startRange);
    const rangeEndStr = formatDate(endRange);

    // Map to track unique IDs to prevent duplicates if query overlaps
    const seenIds = new Set<string>();

    events.forEach(ev => {
        // Simple events (Non-recurring)
        if (!ev.recurrence || ev.recurrence === 'NONE') {
            if (!seenIds.has(ev.id)) {
                expanded.push(ev);
                seenIds.add(ev.id);
            }
            return;
        }

        // Recurring Events (Weekly)
        if (ev.recurrence === 'WEEKLY' && ev.startDate) {
            let current = new Date(ev.startDate);
            const until = ev.recurrenceUntil ? new Date(ev.recurrenceUntil) : endRange; // Default until view end
            const effectiveUntil = until < endRange ? until : endRange;
            
            // Optimization: Advance current to near startRange if needed
            // (Simple iteration is fast enough for typical calendar loads < 1000 items)

            while (current <= effectiveUntil) {
                const currentStr = formatDate(current);
                
                // Only include if within view range
                if (currentStr >= rangeStartStr && currentStr <= rangeEndStr) {
                    const instanceId = `${ev.id}_${currentStr}`;
                    if (!seenIds.has(instanceId)) {
                        expanded.push({
                            ...ev,
                            id: instanceId, // Virtual ID for React Key
                            date: new Date(current),
                            startDate: currentStr,
                            endDate: currentStr, // Weekly instances are typically single day
                            // If original was multi-day recurring, logic would need duration calc
                        });
                        seenIds.add(instanceId);
                    }
                }
                
                // Add 7 days
                current.setDate(current.getDate() + 7);
            }
        }
    });
    return expanded;
}

// --- CRUD ---

export function subscribeCalendarEventsByRange(
  start: Date,
  end: Date,
  onChange: (events: CalendarEvent[]) => void,
  onError?: (err: unknown) => void,
): () => void {
  // 1. Query events falling in range (by date field)
  // Note: This catches non-recurring events and the *first* instance of recurring events
  const qRange = db.collection(CALENDAR_COL)
    .where("date", ">=", toTimestamp(subtractDays(start, 7))) // Buffer
    .where("date", "<=", toTimestamp(end));

  // 2. Query ALL recurring events (to catch those started before range)
  // Assuming low volume of recurring events allowed in system
  const qRecurring = db.collection(CALENDAR_COL)
    .where("recurrence", "==", "WEEKLY");

  let eventsRange: CalendarEvent[] = [];
  let eventsRecurring: CalendarEvent[] = [];

  const mergeAndEmit = () => {
      const combined = [...eventsRange, ...eventsRecurring];
      const expanded = expandWeeklyEvents(combined, start, end);
      onChange(expanded);
  };

  const unsubRange = qRange.onSnapshot(
    (snap) => {
      eventsRange = snap.docs.map(docToEvent);
      mergeAndEmit();
    },
    (err) => { console.error(err); if(onError) onError(err); }
  );

  const unsubRecurring = qRecurring.onSnapshot(
    (snap) => {
      eventsRecurring = snap.docs.map(docToEvent);
      mergeAndEmit();
    },
    (err) => { console.error(err); }
  );

  return () => {
      unsubRange();
      unsubRecurring();
  };
}

export async function createManualCalendarEvent(input: {
  mode: 'ALL_DAY' | 'TIME_SLOT';
  startDate: Date;
  endDate?: Date;
  startTime?: string; // HH:mm
  endTime?: string;   // HH:mm
  title: string;
  relatedRef?: string;
  note?: string;
  createdBy: string;
  recurrence?: 'NONE' | 'WEEKLY';
  recurrenceUntil?: string;
}): Promise<void> {
  const docRef = db.collection(CALENDAR_COL).doc();
  
  let payload: any = {
    title: input.title,
    category: 'MANUAL',
    subType: 'GENERAL',
    sourceType: 'manual',
    sourceId: docRef.id,
    relatedRef: input.relatedRef || "",
    note: input.note || "",
    createdBy: input.createdBy,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    color: '#666', 
    recurrence: input.recurrence || 'NONE',
    recurrenceUntil: input.recurrenceUntil || null,
    autoType: null
  };

  if (input.mode === 'ALL_DAY') {
    const start = new Date(input.startDate); 
    start.setHours(0,0,0,0);
    const end = input.endDate ? new Date(input.endDate) : new Date(start);
    end.setHours(0,0,0,0);

    payload = {
      ...payload,
      mode: 'ALL_DAY_RANGE',
      allDay: true,
      date: toTimestamp(start),
      startDate: formatDate(start),
      endDate: formatDate(end)
    };
  } else {
    const sDateStr = formatDate(input.startDate);
    const startISO = `${sDateStr}T${input.startTime}:00`;
    const endISO = `${sDateStr}T${input.endTime}:00`;
    const startObj = new Date(startISO);

    payload = {
      ...payload,
      mode: 'TIME_RANGE',
      allDay: false,
      date: toTimestamp(startObj),
      startDateTime: startISO,
      endDateTime: endISO,
      startDate: sDateStr,
      endDate: sDateStr
    };
  }

  await docRef.set(payload);
}

// --- Contract Sync Logic ---

function buildContractLabel(prefix: string, contractNo: string, title?: string) {
  const titlePart = title ? ` ${title}` : '';
  return `【${prefix}】 ${contractNo}${titlePart}`;
}

/**
 * Sync Contract Events: Sign Date + Expiry Reminders
 */
export async function syncContractExpiryEvents(contract: {
  id: string;
  contractNo?: string;
  title?: string;
  periodEnd?: string; // YYYY-MM-DD
  signDate?: string;  // YYYY-MM-DD (or periodStart)
  status?: string;
}): Promise<void> {
  const { id, contractNo = "", title = "", periodEnd, signDate } = contract;
  
  // 1. Delete ALL existing events for this contract to ensure clean state
  await deleteContractCalendarEvents(id);

  if (!signDate && !periodEnd) return;

  const batch = db.batch();
  const baseTitle = title;

  const createEvent = (
    dateStr: string,
    label: string,
    autoType: CalendarEventAutoType,
    color: string
  ) => {
    const ref = db.collection(CALENDAR_COL).doc();
    batch.set(ref, {
      title: label,
      startDate: dateStr,
      endDate: dateStr,
      date: toTimestamp(new Date(dateStr)),
      isAllDay: true,
      allDay: true,
      mode: 'ALL_DAY_RANGE',
      sourceType: 'contract',
      sourceId: id,
      autoType,
      repeatType: 'none',
      recurrence: 'NONE',
      relatedCode: contractNo,
      notes: `合約編號：${contractNo}\n標題：${baseTitle}`,
      color: color,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
  };

  // 2. Sign Date Event
  if (signDate) {
      createEvent(
          formatDate(new Date(signDate)), 
          buildContractLabel('合約簽署日', contractNo, baseTitle),
          'contract-sign-date',
          '#10b981' // Green
      );
  }

  // 3. Expiry Events (if not permanent/empty)
  if (periodEnd && periodEnd !== '永久') {
      const end = formatDate(new Date(periodEnd));
      
      createEvent(
          addDays(end, -60),
          buildContractLabel('合約到期前60天', contractNo, baseTitle),
          'contract-expiry-60',
          '#f59e0b' // Orange
      );
      createEvent(
          addDays(end, -30),
          buildContractLabel('合約到期前30天', contractNo, baseTitle),
          'contract-expiry-30',
          '#f59e0b'
      );
      createEvent(
          end,
          buildContractLabel('合約到期日', contractNo, baseTitle),
          'contract-expiry-0',
          '#ef4444' // Red
      );
  }

  await batch.commit();
}

/**
 * Delete all calendar events associated with a contract
 */
export async function deleteContractCalendarEvents(contractId: string): Promise<void> {
  const q = db.collection(CALENDAR_COL)
    .where("sourceType", "==", "contract")
    .where("sourceId", "==", contractId);
  
  const snap = await q.get();
  if (snap.empty) return;

  const batch = db.batch();
  snap.docs.forEach(doc => batch.delete(doc.ref));
  await batch.commit();
}

/**
 * Delete a calendar event by ID.
 */
export async function deleteCalendarEventById(id: string): Promise<void> {
    await db.collection(CALENDAR_COL).doc(id).delete();
}

/**
 * Cleanup orphan events (where contract doc no longer exists)
 */
export async function cleanupOrphanContractEvents() {
  const q = db.collection(CALENDAR_COL).where('sourceType', '==', 'contract');
  const snap = await q.get();
  const batch = db.batch();
  let count = 0;

  // Fetch all unique sourceIds to check existence
  const sourceIds = new Set(snap.docs.map(d => d.data().sourceId).filter(Boolean));
  
  // Note: Checking thousands of docs one by one is slow. 
  // For small/medium scale, we can fetch contracts in batches or just check one by one.
  // Implementation: Check one by one for safety in this tool.
  
  for (const docSnap of snap.docs) {
      const data = docSnap.data();
      if (!data.sourceId) {
          batch.delete(docSnap.ref);
          count++;
          continue;
      }
      
      const contractRef = db.collection("legal_contracts").doc(data.sourceId);
      const cSnap = await contractRef.get();
      if (!cSnap.exists) {
          batch.delete(docSnap.ref);
          count++;
      }
  }

  if (count > 0) {
      await batch.commit();
  }
  console.log(`[CalendarMaintenance] Deleted ${count} orphan contract events.`);
}

/**
 * Sync Contract Review Deadline (Submit + 4 Business Days)
 */
export async function syncContractReviewDueEvent(review: {
  id: string;
  docCode?: string;
  title: string;
  priority?: string;
  submittedAt?: Date | any; // Timestamp
  createdAt?: any;
}): Promise<void> {
  const p = (review.priority || "").toLowerCase();
  const isNormal = p === "normal" || p === "一般" || p === "一般件" || !p;
  
  const q = db.collection(CALENDAR_COL)
    .where("sourceId", "==", review.id)
    .where("category", "==", "CONTRACT_REVIEW_DEADLINE");
  const snap = await q.get();
  const batch = db.batch();
  snap.docs.forEach(doc => batch.delete(doc.ref));

  if (!isNormal) {
    await batch.commit();
    return;
  }

  const baseDate = fromTimestamp(review.submittedAt || review.createdAt || new Date());
  const dueDate = addBusinessDays(baseDate, 4);
  const dateStr = formatDate(dueDate);

  const newRef = db.collection(CALENDAR_COL).doc();
  const label = review.docCode ? `${review.docCode} ${review.title}` : review.title;

  batch.set(newRef, {
    title: `[會審到期] ${label}`,
    category: 'CONTRACT_REVIEW_DEADLINE',
    sourceId: review.id,
    sourceType: 'contractReview',
    autoType: 'contract-review-deadline',
    relatedRef: review.docCode,
    note: `一般件 (3–5天)；提出日：${formatDate(baseDate)}`,
    
    mode: 'ALL_DAY_RANGE',
    allDay: true,
    date: toTimestamp(dueDate),
    startDate: dateStr,
    endDate: dateStr,
    
    color: '#f97316', 
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: 'system'
  });

  await batch.commit();
}

// Legacy helpers maintained for compatibility
export async function getCalendarEventsBetween(startStr: string, endStr: string): Promise<any[]> {
  const startDt = new Date(startStr);
  const endDt = new Date(endStr);
  const q = db.collection(CALENDAR_COL)
    .where("date", ">=", toTimestamp(startDt))
    .where("date", "<=", toTimestamp(endDt));
  const snap = await q.get();
  return snap.docs.map(d => ({id: d.id, ...d.data()}));
}

// --- New Helper Functions for System Event Syncing ---

export async function deleteSystemEventsBySource(sourceType: string, sourceId: string) {
  const q = db.collection(CALENDAR_COL)
    .where("sourceType", "==", sourceType)
    .where("sourceId", "==", sourceId);
  
  const snap = await q.get();
  const batch = db.batch();
  
  if (snap.empty) return;

  snap.docs.forEach(doc => batch.delete(doc.ref));
  await batch.commit();
}

export async function upsertSystemCalendarEvent(params: {
  date: Date;
  title: string;
  sourceType: string;
  sourceId: string;
  kind?: string;
  note?: string;
  color?: string;
  meta?: any;
}) {
  const dateStr = formatDate(params.date);
  const suffix = params.kind ? `-${params.kind}` : "";
  const eventId = `${params.sourceType}-${params.sourceId}${suffix}`;

  // Start of day
  const start = new Date(params.date);
  start.setHours(0,0,0,0);
  // End of day (next day 00:00)
  const end = new Date(start);
  end.setDate(end.getDate() + 1);

  const payload: any = {
    title: params.title,
    date: toTimestamp(start),
    startDate: dateStr,
    endDate: dateStr,
    mode: 'ALL_DAY_RANGE',
    allDay: true,
    
    sourceType: params.sourceType,
    sourceId: params.sourceId,
    subType: params.kind || null,
    
    note: params.note || "",
    color: params.color || "#3b82f6",
    meta: params.meta || null,
    
    autoGenerated: true,
    updatedAt: serverTimestamp()
  };
  
  // Set createdAt if we can implicitly allow it to be overwritten or we rely on it being present from prior set.
  // Using set with merge updates updatedAt but preserves others.
  // To ensure createdAt exists on new, we can set it.
  payload.createdAt = serverTimestamp();
  
  await db.collection(CALENDAR_COL).doc(eventId).set(payload, { merge: true });
}
